import CarouselSwiper, {
    NavigationControls,
    useCarouselSwiperController,
} from '@eventbrite/eds-carousel-swiper';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import TicketSelectionTotal from '../TicketSelectionTotal/TicketSelectionTotal';
import MultiTicketCard, {
    MultiTicketCardProps,
} from './MultiTicketCard/MultiTicketCard';

import './MultiTicket.scss';

export interface MultiTicketProps {
    tickets: MultiTicketCardProps[];
    onQuantityChange: ({
        id,
        quantity,
    }: {
        id: string;
        quantity: number;
    }) => void;
    totalCostDisplay: string | GenericLazyString;
}

export const MultiTicket: FunctionComponent<MultiTicketProps> = ({
    tickets,
    onQuantityChange,
    totalCostDisplay,
}) => {
    const isDesktop = useMedia('(pointer:fine)');
    const [isTextExpanded, setIsTextExpanded] = useState<boolean>(isDesktop);

    useEffect(() => {
        setIsTextExpanded(isDesktop);
    }, [isDesktop]);

    const totalTicketsSelected = tickets.reduce(
        (acc, cur) => (acc += cur.selectedQuantity),
        0,
    );

    const carouselTickets = tickets.map((ticket: any) => {
        const {
            description,
            displayPrice,
            id,
            isDisabled,
            name,
            ticketOptionsRange,
            variantId,
            selectedQuantity,
            quantityRemaining,
            statusKey,
        } = ticket;
        return (
            <MultiTicketCard
                key={id}
                description={description}
                displayPrice={displayPrice.toString()}
                hideTextExpanderToggle={isDesktop}
                isDisabled={isDisabled}
                name={name}
                onQuantityChange={(quantity: number) =>
                    onQuantityChange({ id, quantity })
                }
                quantityRemaining={quantityRemaining}
                selectedQuantity={selectedQuantity}
                ticketOptionsRange={ticketOptionsRange}
                variantId={variantId || id}
                expanded={isTextExpanded}
                onTextDescriptionToggle={setIsTextExpanded}
                statusKey={statusKey}
            />
        );
    });

    const {
        isLastSlide,
        nextSlide,
        prevSlide,
        currentIndex,
        isFirstSlide,
        setCurrentIndex,
    } = useCarouselSwiperController({
        initialSlide: 0,
        items: carouselTickets,
    });

    const ticketSelectionTotal = totalTicketsSelected ? (
        <TicketSelectionTotal
            count={totalTicketsSelected}
            total={totalCostDisplay}
            className="multi-ticket-selection-total"
        />
    ) : null;

    return (
        <div
            data-spec="multi-ticket"
            data-testid="multi-ticket"
            className="multi-ticket"
        >
            <CarouselSwiper
                controls={
                    <NavigationControls
                        className="multi-ticket-navigation-controls"
                        isFirstSlide={isFirstSlide}
                        isLastSlide={isLastSlide}
                        nextSlide={nextSlide}
                        prevSlide={prevSlide}
                        setCurrentIndex={setCurrentIndex}
                        title={
                            <span className="eds-text-weight--bold eds-text-hm">
                                {gettext('Tickets')}
                            </span>
                        }
                    />
                }
                currentIndex={currentIndex}
                gap={'8px'}
                items={carouselTickets}
                progressClassName="multi-ticket-carousel-progress"
                setCurrentIndex={setCurrentIndex}
                viewportClassName="multi-ticket-carousel-viewport"
            />
            {ticketSelectionTotal}
        </div>
    );
};

export default MultiTicket;
